import { QuestionnaireConfig } from 'data/questionnaire-config-type';
import { Inventory } from 'lib/documents';
import { useMemo } from 'react';
import { isCriticalItemFromConfig } from '../../../isCriticalItem';
import { InventoryTableDisplayInventoryFilter } from '../types';

/**
 * Generate the table rows from the inventory data
 */
export function useProcessData(
  parsedConfiguration: QuestionnaireConfig,
  inventory: Inventory,
  inventoryFilterConfig: InventoryTableDisplayInventoryFilter | undefined) {
  return useMemo(() => {
    /**
     * Extract the sections
     * WARNING: Promis contains multiple subsections and Object.keys is not guaranteed to return keys in the same order.
     * This is fine for now because the old Assessment display for Promis works the same way
     */
    const subsections = Object.keys(parsedConfiguration.subsections);

    /**
     * Flatten the questions from each subsection
     */
    const arr = subsections.flatMap(_ => {
      const obj = parsedConfiguration.subsections[_];
      return (
        obj.questions
          .map(question => ({
            question: question.title,
            /**
             * Lookup the inventory response
             */
            response: inventory.data[question.title],
            /**
             * Add metadata from the section
             * Each section can have its own configuration so we store it in the row metadata
             */
            configMetadata: {
              min: obj.min,
              max: obj.max,
              type: obj.type,
              colorsForward: obj.colorsForward,
              iconsForward: obj.iconsForward,
              beforeLabel: obj.beforeLabel,
              afterLabel: obj.afterLabel
            },
            tags: {
              criticalItem: inventoryFilterConfig &&
                isCriticalItemFromConfig({
                  value: inventory.data[question.title],
                  threshold: inventoryFilterConfig.criticalItemConfig.threshold,
                  config: obj
                })
            }
          }))
          /**
           * Filter the data array if the criticalItemConfig setting is enabled
           */
          .filter(_ => inventoryFilterConfig?.criticalItemConfig.enabled
            ? _.tags.criticalItem
            : true
          )
      );
    });
    return arr;
  }, [parsedConfiguration, inventory, inventoryFilterConfig]);
}
